import React, { useState, useEffect, useRef } from "react";
import useTranslation from "next-translate/useTranslation";
import styles from "./Leagues.module.css";
import { FaUser } from "react-icons/fa";
import { FaAward } from "react-icons/fa";
import { FaStar } from "react-icons/fa";
import { FaSignal } from "react-icons/fa";
import { FaTrophy } from "react-icons/fa";
import { FaChessKing } from "react-icons/fa";

import Medal from "../../components/common/medal";
import { Button } from "@/components/ui/button";
import {
  ModeIds,
  VARIANT_CHESS,
  VARIANT_CHESS960,
  TimeControl,
} from "@chesshotel/constants";
import { getModeNameCapitalFirst, firstToUpper } from "../../util/util";
import {
  getLeagueScores,
  useLeagues,
  getGuestDivisionScores,
} from "../../redux/leagues";
import { setLeaguePlay } from "../../redux/lobby";
import Router from "next/router";
import { useUser } from "../../redux/user";
import { saveAndChooseGame } from "../shared/saveAndChooseGame";
import { useSize } from "../../hooks/useSize";
import Spinner from "@/components/ui/spinner";

const ROW_HEIGHT = 42;
const DAY_MS = 1000 * 60 * 60 * 24;
const PROGRESS_ANIMATION_MS = 1000;
let hasPlayedSeasonProgressAnimation = false;

export default function LeaguesBody({ activeMode = ModeIds.BULLET }) {
  const isDesktop = useSize();
  const { t } = useTranslation("common");
  const { t: leaguesT } = useTranslation("common");

  const { leagueScores, divisionScores, leagueScoresLoaded, seasonNr } =
    useLeagues();
  const { isGuest, displayName, memberLoggedIn } = useUser();

  const [endDate] = useState(getNextSeasonDate());
  const [startDate] = useState(() => {
    const d = new Date(endDate);
    d.setMonth(d.getMonth() - 3);
    return d;
  });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [displayProgress, setDisplayProgress] = useState(0);
  const [displayDaysElapsed, setDisplayDaysElapsed] = useState(0);
  const [shouldAnimateProgress, setShouldAnimateProgress] = useState(
    () => !hasPlayedSeasonProgressAnimation,
  );

  const totalSeasonMs = endDate.getTime() - startDate.getTime();
  const elapsedSeasonMs = Math.max(
    0,
    Math.min(totalSeasonMs, nowMs - startDate.getTime()),
  );
  const progressPercent =
    totalSeasonMs > 0 ? (elapsedSeasonMs / totalSeasonMs) * 100 : 100;
  const totalDays = Math.max(1, Math.round(totalSeasonMs / DAY_MS));
  const daysElapsed = Math.max(
    0,
    Math.min(totalDays, Math.floor((nowMs - startDate.getTime()) / DAY_MS)),
  );
  const seasonLabel = `${t("Season")} ${seasonNr} ${endDate.getFullYear()}`;
  const shownProgress = shouldAnimateProgress
    ? displayProgress
    : progressPercent;
  const shownDaysElapsed = shouldAnimateProgress
    ? displayDaysElapsed
    : daysElapsed;

  useEffect(() => {
    const updateNow = () => setNowMs(Date.now());
    updateNow();
    const timer = setInterval(updateNow, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!shouldAnimateProgress) {
      return;
    }

    hasPlayedSeasonProgressAnimation = true;

    const animationStart = performance.now();
    let frameId = 0;

    const tick = (timestamp) => {
      const progress = Math.min(
        (timestamp - animationStart) / PROGRESS_ANIMATION_MS,
        1,
      );
      setDisplayProgress(progressPercent * progress);
      setDisplayDaysElapsed(Math.round(daysElapsed * progress));

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        setShouldAnimateProgress(false);
      }
    };

    frameId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frameId);
  }, [shouldAnimateProgress, progressPercent, daysElapsed]);

  const rank =
    leagueScores[activeMode] && leagueScores[activeMode].rank
      ? leagueScores[activeMode].rank
      : 0;

  useEffect(() => {
    if (memberLoggedIn) {
      getLeagueScores();
    } else {
      getGuestDivisionScores(activeMode);
    }
  }, [memberLoggedIn]);

  useEffect(() => {
    if (leagueScoresLoaded) {
      // Move function declaration outside of block to avoid ES5 strict mode error
      const scrollToMe = () => {
        let rowsFromTop = 0;
        let scrollToRow = 0;
        if (leagueScoresLoaded && ladderEl && ladderEl.current) {
          if (rank < 9) {
            rowsFromTop = 0;
            scrollToRow = 0;
          } else if (rank > 8 && rank < 26) {
            rowsFromTop = Math.max(8 - rank, -4);
            scrollToRow = rank + rowsFromTop;
          } else if (rank < 51) {
            rowsFromTop = Math.max(25 - rank, -4);
            scrollToRow = rank + rowsFromTop;
          } else {
            rowsFromTop = Math.max(50 - rank, -4);
            scrollToRow = rank + rowsFromTop;
          }
          ladderEl.current.scrollTop = scrollToRow * ROW_HEIGHT;
        }
      };
      scrollToMe();
    }
  }, [leagueScoresLoaded, activeMode, rank, divisionScores]);

  useEffect(() => {
    if (isGuest) {
      getGuestDivisionScores(activeMode);
    }
  }, [activeMode]);

  const ladderEl = useRef(null);

  const leagueName =
    leagueScores[activeMode] && leagueScores[activeMode].leagueName;
  const division =
    leagueScores[activeMode] && leagueScores[activeMode].divisionId;

  const getMedal = (i) => {
    const size = isDesktop ? "medium" : "small";

    if (i === 0) {
      return (
        <td className={styles.medal} valign="top" align="center" rowSpan={8}>
          <div className={styles.medalCont}>
            <div className={styles.sticky}>
              <Medal title="TOP" rank={8} leagueName={leagueName} size={size} />
            </div>
          </div>
        </td>
      );
    } else if (i === 8) {
      return (
        <td className={styles.medal} valign="top" align="center" rowSpan={17}>
          <div className={styles.medalCont}>
            <div className={styles.sticky}>
              <Medal
                title="TOP"
                rank={25}
                leagueName={leagueName}
                size={size}
              />
            </div>
          </div>
        </td>
      );
    } else if (i === 25) {
      return (
        <td className={styles.medal} valign="top" align="center" rowSpan={25}>
          <div className={styles.medalCont}>
            <div className={styles.sticky}>
              <Medal
                title="TOP"
                rank={50}
                leagueName={leagueName}
                size={size}
              />
            </div>
          </div>
        </td>
      );
    } else if (i === 50) {
      return (
        <td className={styles.medal} valign="top" align="center" rowSpan={50}>
          <div className={styles.medalCont}>
            <div className={styles.sticky}>
              <Medal
                title="TOP"
                rank={100}
                leagueName={leagueName}
                size={size}
              />
            </div>
          </div>
        </td>
      );
    }

    if (i > 99) {
      return <td className={styles.medalPlaceholder}></td>;
    }

    return null;
  };

  function getNextSeasonDate() {
    const now = new Date();

    const seasonMonths = [0, 3, 6, 9];

    const day = 1;
    const hour = 10;
    const minute = 0;
    const second = 0;

    for (let i = 0; i < seasonMonths.length; i++) {
      const month = seasonMonths[i];
      const nextDate = new Date(
        now.getFullYear(),
        month,
        day,
        hour,
        minute,
        second,
      );

      if (nextDate > now) {
        return nextDate;
      }
    }

    return new Date(now.getFullYear() + 1, 0, 1, hour, minute, second);
  }

  const gotoLobby = () => {
    if (activeMode === ModeIds.PUZZLE) {
      Router.push("/puzzles");
      return;
    }

    setLeaguePlay(true);

    switch (activeMode) {
      case ModeIds.BULLET:
        saveAndChooseGame(2, 1, VARIANT_CHESS, null, TimeControl.LIVE);
        break;
      case ModeIds.BLITZ:
        saveAndChooseGame(5, 2, VARIANT_CHESS, null, TimeControl.LIVE);
        break;
      case ModeIds.RAPID:
        saveAndChooseGame(10, 0, VARIANT_CHESS, null, TimeControl.LIVE);
        break;
      case ModeIds.CHESS960:
        saveAndChooseGame(8, 0, VARIANT_CHESS960, null, TimeControl.LIVE);
        break;
      case ModeIds.DAILY:
        saveAndChooseGame(null, null, VARIANT_CHESS, 1, TimeControl.DAILY);
        break;
      default:
        return null;
    }
    Router.push("/lobby");
  };

  const renderEmpty = () => {
    return (
      <div className="flex flex-col items-center justify-center w-full gap-3">
        {t("Not entered in league")}
        <Button onClick={() => gotoLobby()} size="wide">
          {leaguesT("Join")}
        </Button>
      </div>
    );
  };

  const renderRows = () => {
    const rows = divisionScores[activeMode];

    const len = rows.length;

    const filledRows = rows.map((row, i) => {
      const lineRowClass =
        i === 7 || i === 24 || i === 49 || i === 99 ? styles.lineRow : null;
      let userRowClass = null;

      if (row.username == displayName) {
        userRowClass = styles.userRow;
      }

      return (
        <tr className={lineRowClass + " " + userRowClass} key={i}>
          {getMedal(i)}
          <td>{i + 1}</td>
          <td className={styles.userCol}>{row.username}</td>
          <td>{row.points}</td>
          <td>{row.elo}</td>
          <td>{row.wins}</td>
          <td>{row.losses}</td>
        </tr>
      );
    });

    let emptyRows = 0;
    if (len < 8) {
      emptyRows = 8 - len;
    } else if (len < 16) {
      emptyRows = 16 - len;
    } else if (len < 33 && len >= 25) {
      emptyRows = 33 - len;
    } else if (len < 58 && len >= 50) {
      emptyRows = 58 - len;
    }

    for (let i = 0; i < emptyRows; i++) {
      const firstStyle = i === 0 && styles.topLine;
      filledRows.push(
        <tr className={styles.blank + " " + firstStyle}>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>,
      );
    }
    return filledRows;
  };

  const renderTable = () => {
    return (
      <div className={styles.bodyCont}>
        <div className={styles.header + " text-athens-gray-50 relative"}>
          <div>
            <div className={styles.title}>
              {getModeNameCapitalFirst(activeMode, t)}{" "}
              {firstToUpper(t(leagueName + " league"))}
            </div>
            <div className={styles.division + " text-athens-gray-50/60"}>
              {leaguesT("Division")} {division}
            </div>
          </div>
          <div className="absolute bottom-0 left-0 w-full">
            <div className="px-[30px] pb-1.5 text-xs text-athens-gray-50/90">
              {seasonLabel} ·{" "}
              {t("Day {{current}} of {{total}}", {
                current: shownDaysElapsed,
                total: totalDays,
              })}
            </div>
            <div className="w-full h-1 bg-[rgba(255,255,255,0.15)]">
              <div
                className="h-full bg-red-400"
                style={{
                  width: shownProgress + "%",
                }}
              />
            </div>
          </div>
        </div>
        <div className={styles.body} ref={ladderEl}>
          <div className={styles.tableCont}>
            <table className={styles.ladderTable}>
              <thead>
                <tr className={styles.tableHead}>
                  <th></th>
                  <th>
                    <div className="flex items-center space-x-1">
                      <FaAward className={styles.icon} />
                      <div className={styles.colTitle}>{leaguesT("Rank")}</div>
                    </div>
                  </th>
                  <th>
                    <div className="flex items-center space-x-1">
                      <FaUser className={styles.icon} />
                      <div className={styles.colTitle}>{t("Player")}</div>
                    </div>
                  </th>
                  <th>
                    <div className="flex items-center space-x-1">
                      <FaStar className={styles.icon} />
                      <div className={styles.colTitle}>{t("Points")}</div>
                    </div>
                  </th>
                  <th>
                    <div className="flex items-center space-x-1">
                      <FaSignal className={styles.icon} />
                      <div className={styles.colTitle}>{t("Rating")}</div>
                    </div>
                  </th>
                  <th>
                    <div className="flex items-center space-x-1">
                      <FaTrophy className={styles.icon} />
                      <div className={styles.colTitle}>{t("Wins")}</div>
                    </div>
                  </th>
                  <th>
                    <div className="flex items-center space-x-1">
                      <FaChessKing className={styles.iconRotate} />
                      <div className={styles.colTitle}>{t("Losses")}</div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (!leagueScoresLoaded) {
    return (
      <div className={styles.bodyCont}>
        <div className={styles.header + " text-athens-gray-50"}></div>
        <div className={`${styles.body} flex flex-col justify-center `}>
          <Spinner />
        </div>
      </div>
    );
  } else if (leagueName === "placement" || leagueName == null) {
    return renderEmpty();
  } else {
    return renderTable();
  }
}
